import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import { useChatContext } from '../../../contexts/ChatContextProvider'
import { fetchJurisdictions } from '../../../lib/api/api'
import type { CantonInfo } from '../../../lib/api/api'
import { CANTON_CODES } from '../../../shared/cantons'

// The cantons the cantonal-law source searches (subproject 3e): the user's
// choice (kept in this browser), else the firm's, else Ticino. Cantons not in
// the corpus yet are listed "in arrivo" and cannot be chosen; a canton the
// question names is added by the server for that question. While Ticino is the
// only canton available (CANTONAL_LAWS off, or the server list unreachable)
// there is nothing to choose: the picker is not shown.
export default function CantonPicker() {
  const { t } = useTranslation()
  const { cantons, setCantons } = useChatContext()
  const [info, setInfo] = useState<CantonInfo[] | null>(null)
  const [firmDefault, setFirmDefault] = useState<string[]>(['TI'])

  useEffect(() => {
    let active = true
    fetchJurisdictions()
      .then((res) => {
        if (!active) return
        setInfo(res.data.cantons)
        if (res.data.firm_default?.length) setFirmDefault(res.data.firm_default)
      })
      .catch(() => { /* offline or older server: Ticino only */ })
    return () => { active = false }
  }, [])

  const available = new Set((info ?? []).filter((c) => c.available).map((c) => c.code))
  available.add('TI')
  if (available.size === 1) return null
  const chosen = (cantons ?? firmDefault).filter((code) => available.has(code))
  const shown = chosen.length ? chosen : ['TI']
  const addable = CANTON_CODES.filter((code) => !shown.includes(code))
  const name = (code: string) => t(`cantons.${code}`)

  return (
    <div className="ml-6 mt-1 space-y-1">
      <div className="flex flex-wrap items-center gap-1">
        {shown.map((code) => (
          <span key={code}
            className="inline-flex items-center gap-1 rounded-full bg-primary-50 dark:bg-primary-900/30 px-2 py-0.5 text-xs text-primary-700 dark:text-primary-300">
            {name(code)}
            <button type="button" disabled={shown.length === 1}
              onClick={() => setCantons(shown.filter((c) => c !== code))}
              aria-label={t('chat.context.cantons.remove', { name: name(code) })}
              className="disabled:opacity-30">
              <X className="w-3 h-3" aria-hidden="true" />
            </button>
          </span>
        ))}
      </div>
      <select aria-label={t('chat.context.cantons.add')} value=""
        onChange={(e) => { if (e.target.value) setCantons([...shown, e.target.value]) }}
        className="text-xs border border-gray-300 dark:border-gray-700 rounded-md px-1 py-0.5 bg-white dark:bg-gray-800">
        <option value="">{t('chat.context.cantons.addPlaceholder')}</option>
        {addable.map((code) => (
          <option key={code} value={code} disabled={!available.has(code)}>
            {available.has(code) ? name(code) : t('chat.context.cantons.upcoming', { name: name(code) })}
          </option>
        ))}
      </select>
      {cantons !== null && (
        <button type="button" onClick={() => setCantons(null)} className="block text-xs text-primary-600 hover:underline">
          {t('chat.context.cantons.reset')}
        </button>
      )}
      <p className="text-xs text-gray-500 dark:text-gray-400">{t('chat.context.cantons.hint')}</p>
    </div>
  )
}
